import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useShouldDisplayRoamingHints } from "@/hooks/useShouldDisplayRoamingHints";
import {
  getSimRoamingCallPackageUsage,
  useGetVoiceSettings,
} from "@/services/api";
import {
  GetRoamingPackageUsageResponse,
  RoamingInfo,
  RoamingInfoRoamingStatus,
  SimCardDetail,
} from "@/services/model";
import {
  getTariffName,
  getTariffType,
  stripFlatName,
  TariffTypes,
} from "@/utils/deviceUtils";
import { isFlatMobileTariff } from "@/utils/tariffUtils";

/**
 * Derives every display value needed to render a booked roaming option
 * (mobile-abo included package or a booked travel package), shared by
 * whichever card/list markup currently wraps it.
 */
export const useBookedRoamingOptionDisplay = (
  roamingInfo: RoamingInfo,
  simDetail: SimCardDetail,
  index: number,
) => {
  const { t } = useTranslation("portal");
  const { shouldDisplayPartialHint } = useShouldDisplayRoamingHints({
    roamingInfo,
    serviceId: simDetail.serviceID,
  });
  const [roamingCallPackageUsage, setRoamingCallPackageUsage] =
    useState<GetRoamingPackageUsageResponse>();

  const { data: voiceSettings } = useGetVoiceSettings(
    {
      simId: simDetail.simId || -1,
    },
    {
      query: {
        enabled: true,
      },
    },
  );
  const isRoamingCallsUsageNotAllowed =
    voiceSettings?.reason?.includes("CreditClassC");

  const status = roamingInfo.roamingStatus;
  const tariffType = getTariffType(simDetail.serviceID);

  const isMobileAboRoaming = !!roamingInfo.includedInService;
  const isActive = status === RoamingInfoRoamingStatus.active;
  const isOrdered = status === RoamingInfoRoamingStatus.ordered;

  const hasIncludedRoamingCalls =
    isMobileAboRoaming &&
    (tariffType === TariffTypes.flatMobile ||
      tariffType === TariffTypes.flatMobileSwiss ||
      tariffType === TariffTypes.flatMobilePlus);
  const shouldTreatAsActive = isActive || hasIncludedRoamingCalls;

  const title = isMobileAboRoaming
    ? t("mobileAbo.name")
    : t("cockpit.managementTile.roaming.package", {
        index: index + 1,
      });

  const name = isMobileAboRoaming
    ? getTariffName(simDetail)
    : stripFlatName(roamingInfo.shortName);
  const flatName = isOrdered ? "-" : name;

  const isTravelPackage =
    !isFlatMobileTariff(name) || !roamingInfo.includedInService;

  useEffect(() => {
    // Clear eagerly so a SIM switch never keeps showing the previous SIM's
    // usage while the new request is in flight (or isn't made at all, e.g.
    // the new tariff has unlimited or no included roaming calls).
    setRoamingCallPackageUsage(undefined);

    const hasLimitedRoamingCalls =
      tariffType === TariffTypes.flatMobile ||
      tariffType === TariffTypes.flatMobileSwiss;

    if (!hasLimitedRoamingCalls || !simDetail.simId) {
      return;
    }

    let isCurrent = true;
    getSimRoamingCallPackageUsage({ simId: simDetail.simId }).then((data) => {
      // The SIM (or its tariff) may have changed again while this request
      // was in flight - a late response for an obsolete SIM must not
      // overwrite the usage already showing for the current one.
      if (!isCurrent) {
        return;
      }
      setRoamingCallPackageUsage({
        roamingCalls: data.roamingCalls ? Math.ceil(data.roamingCalls / 60) : 0,
        maxInclRoamingCalls: data.maxInclRoamingCalls
          ? data.maxInclRoamingCalls / 60
          : 0,
      });
    });

    return () => {
      isCurrent = false;
    };
  }, [simDetail.simId, tariffType]);

  return {
    shouldDisplayPartialHint,
    roamingCallPackageUsage,
    isRoamingCallsUsageNotAllowed,
    tariffType,
    isMobileAboRoaming,
    isOrdered,
    hasIncludedRoamingCalls,
    shouldTreatAsActive,
    title,
    flatName,
    isTravelPackage,
  };
};
